import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  SEATS, SEAT_NAMES, admin4, apply4, botMove4, createGame4, eliminate4, moveText4, onBoard4, xy4,
  type Admin4, type Game4Snapshot, type Kind4, type Move4, type Seat, type State4,
} from '@ic/engine';
import { NukeOverlay, type NukePiece } from '../components/AdminPanel';
import { Board4, ICON4, place } from '../components/Board4';
import { SignupWall } from '../components/SignupWall';
import { countGuestGame, useGuestQuota } from '../lib/guest';
import { Confetti } from '../components/Confetti';
import { Icon } from '../components/Icons';
import { LeaveGuard } from '../components/LeaveGuard';
import { Loader } from '../components/Loader';
import { PieceIcon } from '../components/Pieces';
import { auth } from '../lib/auth';
import { onMessage, send } from '../lib/socket';
import { sfx } from '../lib/sound';
import { settings, useStore } from '../lib/store';
import { tcLabel } from './Play';

interface View {
  state: State4;
  you: Seat | null;
  names: Record<Seat, string>;
  ratings: Partial<Record<Seat, number | null>>;
  clocks: Record<Seat, number> | null;
  running: Seat | null;
  moves: { seat: Seat; text: string }[];
  subtitle: string;
  ratingDelta?: Partial<Record<Seat, number>>;
}

const fmt = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Offline: you are White against three bots. */
function useLocal4() {
  const [state, setState] = useState<State4>(() => createGame4());
  const [moves, setMoves] = useState<{ seat: Seat; text: string }[]>([]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const push = (next: State4, text: string | null, seat: Seat) => {
    if (text) setMoves((m) => [...m, { seat, text }]);
    setState(next);
  };
  useEffect(() => {
    if (state.result || state.turn === 'w') return;
    const t = setTimeout(() => {
      const s = stateRef.current;
      const m = botMove4(s);
      if (m) push(apply4(s, m), moveText4(s, m), s.turn);
    }, 500 + Math.random() * 500);
    return () => clearTimeout(t);
  }, [state]);
  return {
    view: {
      state, you: 'w' as Seat, moves, subtitle: 'Offline · you against three computers',
      names: { w: 'You', g: 'Green bot', b: 'Black bot', y: 'Yellow bot' }, ratings: {}, clocks: null, running: null,
    } satisfies View,
    move: (m: Move4) => push(apply4(state, m), moveText4(state, m), state.turn),
    resign: () => push(eliminate4(state, 'w', 'resignation'), null, 'w'),
    admin: (a: Admin4) => push(admin4(state, 'w', a), a.kind === 'spawn' ? `+${a.piece}` : a.kind === 'nuke' ? 'NUKE' : 'admin win', 'w'),
    restart: () => { setState(createGame4()); setMoves([]); },
  };
}

function useOnline4(id: string) {
  const [g, setG] = useState<Game4Snapshot | null>(null);
  const [recvAt, setRecvAt] = useState(Date.now());
  const [err, setErr] = useState('');
  const [nukeSignal, setNukeSignal] = useState(0);
  const [nukeBy, setNukeBy] = useState<Seat | null>(null);
  useEffect(() => {
    const off = onMessage((m) => {
      if (m.t === 'nuke4Start' && m.gameId === id) { setNukeBy(m.by); setNukeSignal(Date.now()); }
      if (m.t === 'game4' && m.game.id === id) { setG(m.game); setRecvAt(Date.now()); setErr(''); }
      if (m.t === 'error') setErr(m.message);
    });
    send({ t: 'join4', gameId: id });
    return () => { off(); send({ t: 'leave4', gameId: id }); };
  }, [id]);
  const view: View | null = g && {
    state: g.state, you: g.you, moves: g.moves, ratingDelta: g.ratingDelta,
    subtitle: `Online · ${tcLabel(g.tc)} · ${g.rated ? 'rated' : 'casual'}`,
    names: Object.fromEntries(SEATS.map((s) => [s, g.players[s].name])) as Record<Seat, string>,
    ratings: Object.fromEntries(SEATS.map((s) => [s, g.players[s].rating])),
    clocks: g.tc.initial > 0 ? g.clocks : null, running: g.running,
  };
  return {
    view, err, recvAt, nukeSignal, nukeBy,
    move: (move: Move4) => send({ t: 'move4', gameId: id, move }),
    resign: () => send({ t: 'resign4', gameId: id }),
    admin: (action: Admin4) => send({ t: 'admin4', gameId: id, action }),
  };
}

export function FourGamePage() {
  const { id = 'local' } = useParams();
  return id === 'local' ? <LocalFour /> : <OnlineFour id={id} />;
}

function LocalFour() {
  const g = useLocal4();
  const quota = useGuestQuota();
  const lockedAtStart = useRef(quota.guest && quota.left === 0);
  const counted = useRef(false);
  const ply = g.view.state.ply;
  useEffect(() => {
    if (ply === 0) counted.current = false;
    else if (!counted.current) { counted.current = true; countGuestGame(); }
  }, [ply]);
  if (lockedAtStart.current) return <SignupWall />;
  return <FourView view={g.view} recvAt={Date.now()} onMove={g.move} onResign={g.resign} onAdmin={g.admin} onRestart={g.restart} />;
}

function OnlineFour({ id }: { id: string }) {
  const g = useOnline4(id);
  if (!g.view) return <div className="page page-loading">{g.err ? <div className="notice error">{g.err}</div> : <Loader label="Connecting to the game…" />}</div>;
  return <FourView view={g.view} recvAt={g.recvAt} err={g.err} onMove={g.move} onResign={g.resign} onAdmin={g.admin} remoteNuke nukeSignal={g.nukeSignal} nukeBy={g.nukeBy} />;
}

function FourView({ view, recvAt, err, onMove, onResign, onAdmin, onRestart, remoteNuke, nukeSignal, nukeBy }: {
  view: View; recvAt: number; err?: string;
  onMove: (m: Move4) => void; onResign: () => void; onAdmin: (a: Admin4) => void; onRestart?: () => void;
  /** Online: the server runs the nuke timeline for everybody. */
  remoteNuke?: boolean; nukeSignal?: number; nukeBy?: Seat | null;
}) {
  const nav = useNavigate();
  const { admin } = useStore(auth);
  const { pieceSet } = useStore(settings);
  const [now, setNow] = useState(Date.now());
  const [spawn, setSpawn] = useState<Kind4 | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const { state, you } = view;
  const [nuke, setNuke] = useState<{ run: number; pieces: NukePiece[] } | null>(null);
  // Everything on the board is caught, except the king of whoever launched it.
  const nukePieces = (by: Seat): NukePiece[] => state.board.flatMap((p, sq) => {
    if (!p || (p.k === 'K' && p.c === by)) return [];
    const [col, row] = place(sq, you ?? 'w');
    return [{ sq, x: ((col + 0.5) / 14) * 100, y: ((row + 0.5) / 14) * 100, size: 100 / 14, icon: ICON4[p.k], color: p.c }];
  });
  useEffect(() => {
    if (!nukeSignal) return;
    setNuke({ run: nukeSignal, pieces: nukePieces(nukeBy ?? you ?? 'w') });
  }, [nukeSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  const playing = !!you && state.alive.includes(you) && !state.result;
  const myTurn = playing && state.turn === you;

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 200); return () => clearInterval(t); }, []);
  const lastPly = useRef(state.ply);
  useEffect(() => {
    if (state.ply === lastPly.current) return;
    lastPly.current = state.ply;
    const captured = state.last && state.board[state.last.to] && view.moves[view.moves.length - 1]?.text.includes('x');
    if (captured) sfx.capture(); else sfx.move();
    if (state.alive.some((c) => c === you) && state.turn === you) sfx.check();
  }, [state.ply]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (state.result) sfx.end(); }, [state.result]);

  const clock = (s: Seat) => {
    if (!view.clocks) return null;
    return view.running === s ? view.clocks[s] - (now - recvAt) : view.clocks[s];
  };
  const outReason = (s: Seat) => state.out.find((o) => o.seat === s)?.reason;
  const won = !!state.result?.winner && state.result.winner === you;
  const pick = useMemo(() => spawn && playing ? {
    can: (sq: number) => { const [x, y] = xy4(sq); return onBoard4(x, y) && !state.board[sq]; },
    onPick: (sq: number) => { onAdmin({ kind: 'spawn', piece: spawn, sq }); setSpawn(null); },
  } : null, [spawn, playing, state, onAdmin]);

  return (
    <div className="page game-page">
      <LeaveGuard active={playing} onLeave={onResign} text="Leaving now counts as giving up: you are out of this game." />
      <Confetti fire={won ? `four:${state.ply}` : null} />
      <div className="game-layout">
        <div className="board-col">
          <div className="board4-holder">
            <Board4 state={state} seat={you ?? 'w'} interactive={myTurn && !spawn && !nuke} onMove={onMove} pick={pick} />
            {nuke && <NukeOverlay run={nuke.run} phase="full" pieces={nuke.pieces} onImpact={remoteNuke ? undefined : () => onAdmin({ kind: 'nuke' })} onDone={() => setNuke(null)} />}
            {state.result && !dismissed && !nuke && (
              <div className="result-modal" role="dialog">
                <div className="result-card">
                  <h2>{won ? 'You won!' : state.result.winner ? `${view.names[state.result.winner]} won` : 'Draw'}</h2>
                  <p className="muted">by {state.result.reason}</p>
                  {you && view.ratingDelta?.[you] != null && <p>Rating {view.ratingDelta[you]! >= 0 ? '+' : ''}{view.ratingDelta[you]}</p>}
                  <div className="row gap">
                    {onRestart && <button className="btn primary" onClick={() => { onRestart(); setDismissed(false); }}>Play again</button>}
                    <button className="btn secondary" onClick={() => nav('/play/setup')}>New game</button>
                    <button className="btn ghost" onClick={() => setDismissed(true)}>View board</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <aside className="side-panel">
          <div className="panel-head">
            <div className="grow">
              <h1 className="panel-title">4 players</h1>
              <div className="muted small">{view.subtitle}</div>
            </div>
          </div>
          <div className={`status-line ${state.result ? 'over' : ''}`}>
            {state.result ? (state.result.winner ? `${SEAT_NAMES[state.result.winner]} wins by ${state.result.reason}` : `Draw (${state.result.reason})`)
              : myTurn ? 'Your move' : `${view.names[state.turn]} to move`}
          </div>
          <div className="seat-list">
            {SEATS.map((s) => {
              const ms = clock(s);
              const out = outReason(s);
              return (
                <div key={s} className={`seat-row ${!state.result && state.turn === s ? 'turn' : ''} ${out ? 'out' : ''}`}>
                  <span className={`seat-dot ${s}`} title={SEAT_NAMES[s]} />
                  <span className="seat-name">{view.names[s]}{s === you && view.names[s] !== 'You' ? ' (you)' : ''}
                    {view.ratings[s] != null && <span className="muted small"> {view.ratings[s]}</span>}</span>
                  {out ? <span className="muted small">out · {out}</span> : ms != null && <span className="seat-clock">{fmt(ms)}</span>}
                </div>
              );
            })}
          </div>
          {admin && playing && (
            <div className="admin-panel">
              <div className="admin-head"><Icon name="gear" size={14} /> Admin</div>
              <div className="admin-buttons">
                <button className="btn danger sm" onClick={() => { if (confirm('Drop a nuke? Every piece except your king is destroyed and you win.')) { if (remoteNuke) onAdmin({ kind: 'nuke' }); else setNuke({ run: Date.now(), pieces: nukePieces(you ?? 'w') }); } }}>☢ Nuke</button>
                <button className="btn secondary sm" onClick={() => { if (confirm('Win this game now?')) onAdmin({ kind: 'win' }); }}><Icon name="check" size={14} /> Win</button>
              </div>
              <div className="spawn-types">
                {(['Q', 'R', 'B', 'N', 'P'] as Kind4[]).map((k) => (
                  <button key={k} type="button" className={spawn === k ? 'on' : ''} title={`Spawn ${k}`} onClick={() => setSpawn(spawn === k ? null : k)}>
                    <PieceIcon icon={({ Q: 'queen', R: 'rook', B: 'bishop', N: 'knight', P: 'pawn' } as const)[k as 'Q']} color={you!} set={pieceSet} />
                  </button>
                ))}
              </div>
              {spawn && <span className="muted small">Click an empty square.</span>}
            </div>
          )}
          {err && <div className="error small" style={{ margin: '0 14px' }}>{err}</div>}
          <div className="tab-body">
            <div className="moves4">
              {view.moves.length === 0 && <span className="muted">No moves yet.</span>}
              {view.moves.map((m, i) => <span key={i}><span className={`seat-dot ${m.seat}`} style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 4 }} />{m.text}</span>)}
            </div>
          </div>
          <div className="controls">
            {playing && <button className="btn danger" onClick={() => { if (confirm('Resign? You are out of this game.')) onResign(); }}><Icon name="flag" size={16} /> Resign</button>}
            {onRestart && <button className="btn ghost" onClick={onRestart}><Icon name="restart" size={16} /> Restart</button>}
            {!playing && <button className="btn secondary" onClick={() => nav('/play/setup')}>New game</button>}
          </div>
        </aside>
      </div>
    </div>
  );
}
